import { useState, useEffect, useCallback } from "react";

const STOCK_REPORT_KEY = "stock_report";
const STRATEGY_REPORT_KEY = "strategy_report";
const SETTINGS_REPORT_KEY = "settings_report";

// The settings keys the client picker writes its selection under, suffixed
// with the report type (`client_id_strategy`). Homed here rather than in the
// picker because the strategy report's own store is scoped on them below, and
// a second spelling of either is how the two come to disagree about which
// client is open.
export const SETTINGS_CLIENT_ID_KEY = "client_id";
export const SETTINGS_ACCOUNT_IDS_KEY = "account_ids";

/**
 * Get the entire report object from localStorage
 */
const getReportObject = (
  storageKey: string,
): Record<string, unknown> => {
  try {
    const item = window.localStorage.getItem(storageKey);
    if (item === null) return {};
    const parsed = JSON.parse(item);
    // `JSON.parse` returns whatever was stored, and the annotation above is a
    // claim about it rather than a check of it. A truncated or hand-edited
    // entry can parse to null, to a number, or to an array, and indexing the
    // first of those throws before any figure is read.
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    return parsed;
  } catch (error) {
    console.warn(`Error reading localStorage key "${storageKey}":`, error);
    return {};
  }
};

/**
 * Read one report value out of its store, keeping only what matches the
 * `number | string` contract these hooks declare.
 *
 * Shared by all three hooks below: the validation has to hold for every report
 * surface, and a third copy of it is where one of them drifts.
 *
 * This checks the JSON TYPE, not that the value is a figure -- every string
 * survives, so "abc" still travels on and it is `toFiniteNumber` at the point
 * of use that decides whether it is a number. What is rejected here is what
 * could never be a figure in any reading: an object, an array, a boolean, a
 * null. In particular "" survives on purpose -- it is not corruption but the
 * sentinel the report layer writes for a figure the backend produced none of,
 * which the cards read as "no figure" and render as N/A.
 *
 * Read as an OWN property. The type check below is what actually makes a
 * polluted `Object.prototype` unreachable (every member of it is an object or
 * a function), but a lookup that cannot walk the chain at all does not depend
 * on that reasoning holding.
 */
const readReportValue = (
  storageKey: string,
  key: string,
  defaultValue: number | string,
): number | string => {
  if (!key) return defaultValue;

  const report = getReportObject(storageKey);
  if (!Object.prototype.hasOwnProperty.call(report, key)) {
    return defaultValue;
  }

  const stored = report[key];
  if (typeof stored === "number" || typeof stored === "string") {
    return stored;
  }
  return defaultValue;
};

/**
 * Set a value in the report object in localStorage
 */
const setReportValue = (
  storageKey: string,
  key: string,
  value: number | string,
): void => {
  try {
    const report = getReportObject(storageKey);
    report[key] = value;
    window.localStorage.setItem(storageKey, JSON.stringify(report));

    // Dispatch custom event to sync across components
    window.dispatchEvent(
      new CustomEvent("reportStorageChange", {
        detail: { storageKey, key, value },
      }),
    );
  } catch (error) {
    console.error(`Error setting localStorage key "${storageKey}":`, error);
  }
};

/**
 * One value of one report store, kept in step with every other hook reading
 * the same value. Shared by the three hooks below; only the store differs.
 *
 * `storageKey` may change while mounted -- the strategy report's does when the
 * client changes -- and the value is then re-read from the new store in the
 * same render. Waiting for an effect would show the previous client's figure
 * for a render, and anything reading the value in that render (the PDF
 * request, the drawdown limits) would take it as this client's. The value is
 * held together with the store it was read from so that a mismatch is
 * detectable at all.
 */
const useReportValue = (
  storageKey: string,
  key: string,
  defaultValue: number | string,
): [number | string, (value: number | string) => void] => {
  const [stored, setStored] = useState(() => ({
    storageKey,
    value: readReportValue(storageKey, key, defaultValue),
  }));

  let current = stored;
  if (stored.storageKey !== storageKey) {
    current = {
      storageKey,
      value: readReportValue(storageKey, key, defaultValue),
    };
    setStored(current);
  }

  // Writes to the store this setter was made for. A run that lands after the
  // client changed therefore files its figures under the client it was run
  // for, and the stale setter's state update is discarded by the check above
  // rather than shown under the new client.
  const setValue = useCallback(
    (value: number | string) => {
      if (!key) return;

      setStored({ storageKey, value });
      setReportValue(storageKey, key, value);
    },
    [storageKey, key],
  );

  // Listen for changes from other components
  useEffect(() => {
    if (!key) return;

    const handleStorageChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        storageKey: string;
        key: string;
        value: number | string;
      }>;
      if (
        customEvent.detail.storageKey === storageKey &&
        customEvent.detail.key === key
      ) {
        setStored({ storageKey, value: customEvent.detail.value });
      }
    };

    window.addEventListener("reportStorageChange", handleStorageChange);

    return () => {
      window.removeEventListener("reportStorageChange", handleStorageChange);
    };
  }, [storageKey, key]);

  return [current.value, setValue];
};

/**
 * Hook to manage stock report values in localStorage
 * @param key - The key within the stock_report object
 * @param defaultValue - Default value if key doesn't exist
 * @returns [value, setValue] - Current value and setter function
 */
export const useStockReportValue = (
  key: string,
  defaultValue: number | string,
): [number | string, (value: number | string) => void] =>
  useReportValue(STOCK_REPORT_KEY, key, defaultValue);

// The strategy report's client selection, read from the SETTINGS STORE the
// picker writes rather than from `settingsInfo`: the settings context lives
// inside the report's tab panel, which unmounts on a tab switch, and starts
// empty on every remount -- keying on it would read as a client change on
// every tab switch. This store is read synchronously on mount and follows the
// picker's own write, so it is steady across the remount and current the
// moment a client is actually switched.
const useStrategyReportSelection = () => {
  const [clientId] = useSettingsReportValue(
    `${SETTINGS_CLIENT_ID_KEY}_strategy`,
    "",
  );
  const [accountIds] = useSettingsReportValue(
    `${SETTINGS_ACCOUNT_IDS_KEY}_strategy`,
    "[]",
  );
  return { clientId, scopeKey: `${clientId}:${accountIds}` };
};

/**
 * WHICH strategy report is open: the client and the accounts picked for it.
 * Everything on that report that must not cross to another client keys on
 * this -- the persisted figures below, and the session-held ones
 * (useUncalculatedReportValue, useBeginningConcentration).
 */
export const useStrategyReportScope = (): string =>
  useStrategyReportSelection().scopeKey;

/**
 * Hook to manage strategy report values in localStorage
 *
 * The store is SCOPED TO THE REPORT (useStrategyReportScope): one object per
 * client and account selection, `strategy_report:<client>:<accounts>`. A
 * single global object handed client A's figures to client B wherever B's run
 * did not overwrite them -- an OEA Max Drop the backend could not calculate,
 * a Max Stock Drop for a run with no protect asset, a figure typed by hand --
 * and they reached B's PDF as B's own. Returning to A reads A's store again,
 * typed figures included.
 *
 * With no client picked, the report has nothing to calculate and the hook
 * uses the bare `strategy_report` object. That is also where every figure
 * stored before this scoping lives, and it is never read once a client is
 * picked -- those figures belong to whichever client was last on screen.
 *
 * @param key - The key within the report's strategy_report object
 * @param defaultValue - Default value if key doesn't exist
 * @returns [value, setValue] - Current value and setter function
 */
export const useStrategyReportValue = (
  key: string,
  defaultValue: number | string,
): [number | string, (value: number | string) => void] => {
  const { clientId, scopeKey } = useStrategyReportSelection();
  const storageKey =
    clientId === "" ? STRATEGY_REPORT_KEY : `${STRATEGY_REPORT_KEY}:${scopeKey}`;
  return useReportValue(storageKey, key, defaultValue);
};

/**
 * Hook to manage settings report values in localStorage
 * @param key - The key within the settings_report object
 * @param defaultValue - Default value if key doesn't exist
 * @returns [value, setValue] - Current value and setter function
 */
export const useSettingsReportValue = (
  key: string,
  defaultValue: number | string,
): [number | string, (value: number | string) => void] =>
  useReportValue(SETTINGS_REPORT_KEY, key, defaultValue);
